import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {Dropdown, useTranslationContext} from 'platform/components';
import {Show} from 'platform/foundation';

import {useState, type ReactNode} from 'react';

import {isTruthy} from 'ramda-adjunct';

import {suffixTestId} from 'shared/web';

import {PRESET_DROPDOWN_STYLES} from '../../constants/PRESET_DROPDOWN_STYLES';
import {getPresetColorClasses} from '../../utils/getPresetColorClasses';
import {resolvePresetColor} from '../../utils/resolvePresetColor';
import {PresetActionsButton} from '../PresetActionsButton/PresetActionsButton';
import {PresetPinIcon} from '../PresetPinIcon/PresetPinIcon';
import {PresetSelectionIndicator} from '../PresetSelectionIndicator/PresetSelectionIndicator';

type PresetRowProps = {
  id: string;
  label: string;
  color?: string;
  selected: boolean;
  pinned: boolean;
  isDraggable: boolean;
  count: number | undefined;
  onClick: () => void;
  actions?: ReactNode | ((closeMenu: () => void) => ReactNode);
  copyIsLoading?: boolean;
  onTogglePin?: () => void;
  'data-testid'?: string;
};

export function PresetRow(props: PresetRowProps) {
  const t = useTranslationContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id: props.id,
    disabled: !props.isDraggable,
  });
  const pinLabel = t(props.pinned ? 'general.actions.unpin' : 'general.actions.pin');

  return (
    <div
      ref={setNodeRef}
      role="option"
      aria-selected={props.selected}
      onClick={props.onClick}
      className={`${PRESET_DROPDOWN_STYLES.row} ${props.selected ? `${getPresetColorClasses(props.color, true)} ${PRESET_DROPDOWN_STYLES.selectedRow}` : PRESET_DROPDOWN_STYLES.inactiveRow}`}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.45 : 1,
      }}
      data-testid={props['data-testid']}
    >
      <span
        className={`flex shrink-0 text-[color:var(--preset-menu-muted)] opacity-60 hover:opacity-100 ${props.isDraggable ? 'cursor-grab' : 'cursor-default'}`}
        {...(props.isDraggable ? {...attributes, ...listeners} : {})}
      >
        <GripIcon />
      </span>

      <span
        aria-hidden
        className="size-2.5 shrink-0 rounded-full"
        style={{backgroundColor: resolvePresetColor(props.color)}}
        data-testid={suffixTestId('color', props)}
      />

      <span className="flex min-w-0 flex-1 items-center gap-2">
        <PresetSelectionIndicator
          active={props.selected}
          data-testid={suffixTestId('active-indicator', props)}
        />
        <span className="truncate">{props.label}</span>
        <Show when={props.count !== undefined}>
          <span className={`${PRESET_DROPDOWN_STYLES.count} ${props.selected ? 'opacity-80' : ''}`}>
            {props.count}
          </span>
        </Show>
      </span>

      <Show when={props.onTogglePin}>
        {(onTogglePin) => (
          <button
            type="button"
            title={pinLabel}
            aria-label={pinLabel}
            aria-pressed={props.pinned}
            onClick={(event) => {
              event.stopPropagation();
              onTogglePin();
            }}
            className={`${PRESET_DROPDOWN_STYLES.actionButton} ${props.pinned ? PRESET_DROPDOWN_STYLES.pinned : PRESET_DROPDOWN_STYLES.unpinned}`}
          >
            <span className="flex rotate-45">
              <PresetPinIcon active={false} />
            </span>
          </button>
        )}
      </Show>

      <Show when={isTruthy(props.actions)}>
        <span
          onClick={(event) => event.stopPropagation()}
          className="flex shrink-0"
          data-testid={suffixTestId('preset-actions-dropdown', props)}
        >
          <Dropdown
            isLazy
            strategy="fixed"
            isOpen={isMenuOpen}
            onOpen={() => setIsMenuOpen(true)}
            onClose={() => {
              if (!props.copyIsLoading) {
                setIsMenuOpen(false);
              }
            }}
            dropdownControl={
              <PresetActionsButton
                variant="option"
                isExpanded={isMenuOpen}
                data-testid={suffixTestId('actions-iconButton', props)}
              />
            }
          >
            <Show when={isMenuOpen}>
              {typeof props.actions === 'function'
                ? props.actions(() => setIsMenuOpen(false))
                : props.actions}
            </Show>
          </Dropdown>
        </span>
      </Show>
      <Show when={!isTruthy(props.actions)}>
        <span aria-hidden className="size-5 shrink-0" />
      </Show>
    </div>
  );
}

function GripIcon() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 12 12"
      fill="currentColor"
      aria-hidden="true"
      className="shrink-0"
    >
      <circle cx="4" cy="2" r="1" />
      <circle cx="8" cy="2" r="1" />
      <circle cx="4" cy="6" r="1" />
      <circle cx="8" cy="6" r="1" />
      <circle cx="4" cy="10" r="1" />
      <circle cx="8" cy="10" r="1" />
    </svg>
  );
}
